import { AlertTriangle, Box, CheckCircle2, Download, MapPin, PackageCheck, Plus, Ruler, Truck, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { EmptyState } from "../components/EmptyState";
import {
  CustomItemDetailsFields,
  customItemDetailsFromRecord,
  customItemPayload,
  type CustomItemDetailsValue
} from "../components/CustomItemDetailsFields";
import { dimensionsFromRecord } from "../components/DimensionCubeFields";
import { Toast } from "../components/Toast";
import { InventoryVisual } from "../components/InventoryVisual";
import { useAuth } from "../AuthContext";
import { inventoryAppearance } from "../inventoryDisplay";
import type { DashboardSummary, InventoryUnit } from "../types";

export function DashboardPage() {
  const { user } = useAuth();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [units, setUnits] = useState<InventoryUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingUnit, setEditingUnit] = useState<InventoryUnit | null>(null);
  const [toast, setToast] = useState("");
  const metrics: Array<{
    label: string;
    value: number | string;
    icon: LucideIcon;
    tone: string;
  }> = [
    { label: "Total inventory", value: summary?.total ?? (loading ? "…" : "—"), icon: Box, tone: "blue" },
    { label: "Packed", value: summary?.packed ?? (loading ? "…" : "—"), icon: CheckCircle2, tone: "teal" },
    { label: "Loaded", value: summary?.loaded ?? (loading ? "…" : "—"), icon: Truck, tone: "blue" },
    { label: "Delivered", value: summary?.delivered ?? (loading ? "…" : "—"), icon: MapPin, tone: "amber" },
    { label: "Verified", value: summary?.verified ?? (loading ? "…" : "—"), icon: PackageCheck, tone: "teal" },
    { label: "Warnings", value: summary?.warnings ?? (loading ? "…" : "—"), icon: AlertTriangle, tone: "red" }
  ];

  useEffect(() => {
    Promise.all([api.dashboard() as Promise<DashboardSummary>, api.listInventory()])
      .then(([nextSummary, nextUnits]) => {
        setSummary(nextSummary);
        setUnits(nextUnits.slice(0, 12));
      })
      .catch((reason) => setError(reason.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="page">
      <div className="page-heading">
        <div>
          <h1>Dashboard</h1>
          <p>Current position of every container in the move.</p>
        </div>
        <div className="heading-actions">
          <Link className="button primary" to="/inventory/new">
            <Plus size={18} /> New inventory
          </Link>
          <Link className="button secondary" to="/operations">
            <Download size={18} /> Export
          </Link>
        </div>
      </div>
      {error && <div className="alert error">{error}</div>}
      {summary?.export_warning && (
        <div className="alert warning">
          <AlertTriangle size={20} />
          Export needs attention: {summary.export_warning}
        </div>
      )}
      <section className="metric-strip" aria-label="Move summary" aria-busy={loading}>
        {metrics.map(({ label, value, icon: Icon, tone }) => (
          <article className="metric" key={label}>
            <Icon className={`tone-${tone}`} size={30} strokeWidth={1.8} />
            <strong>{String(value)}</strong>
            <span>{label}</span>
          </article>
        ))}
      </section>
      <section className="section-heading">
        <h2>Inventory overview</h2>
        <Link to="/inventory">View all</Link>
      </section>
      {loading ? (
        <div className="loading-row" role="status">
          Loading inventory…
        </div>
      ) : units.length ? (
        <div className="table-wrap">
          <table className="inventory-table">
            <thead>
              <tr>
                <th>Stock number</th>
                <th>Name</th>
                <th>Location</th>
                <th>Destination</th>
                <th>Status</th>
                <th>Warnings</th>
                <th>Last update</th>
              </tr>
            </thead>
            <tbody>
              {units.map((unit) => (
                <tr key={unit.id}>
                  <td>
                    <Link to={`/inventory/${unit.id}`}>{unit.stock_number}</Link>
                  </td>
                  <td className="inventory-title-cell">
                    <div className="inventory-title-with-icon">
                      <InventoryVisual unit={unit} compact />
                      <span>
                        <strong>{unit.name}</strong>
                        {inventoryAppearance(unit) && <small>{inventoryAppearance(unit)}</small>}
                      </span>
                    </div>
                    {user?.role !== "viewer" && unit.inventory_type !== "TOTE" && (
                      <button className="table-edit-button" onClick={() => setEditingUnit(unit)}>
                        <Ruler size={14} />
                        Edit size &amp; color
                      </button>
                    )}
                  </td>
                  <td>{unit.current_location?.name || "Not set"}</td>
                  <td>{unit.destination_room?.name || "Not set"}</td>
                  <td>
                    <span className={`status status-${unit.move_status.toLowerCase().replaceAll(" ", "-")}`}>
                      {unit.move_status}
                    </span>
                  </td>
                  <td className="warning-cell">
                    {unit.fragile && <span title="Fragile">Fragile</span>}
                    {unit.heavy && <span title="Heavy">Heavy</span>}
                    {unit.keep_upright && <span title="Keep upright">Upright</span>}
                    {unit.do_not_stack && <span title="Do not stack">No stack</span>}
                    {unit.temperature_sensitive && <span title="Temperature sensitive">Temp sensitive</span>}
                    {unit.lithium_batteries && <span title="Lithium batteries">Lithium battery</span>}
                  </td>
                  <td>{formatTime(unit.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState
          title="No inventory yet"
          description="Create the first moving unit, then provision and attach its NFC tag."
          action={
            <Link className="button primary" to="/inventory/new">
              Create inventory
            </Link>
          }
        />
      )}
      {editingUnit && (
        <DashboardCustomEditor
          unit={editingUnit}
          onClose={() => setEditingUnit(null)}
          onSave={async (details) => {
            try {
              const updated = await api.updateInventory(editingUnit.id, {
                version: editingUnit.version,
                ...customItemPayload(details)
              });
              setUnits((current) => current.map((unit) => unit.id === updated.id ? updated : unit));
              setEditingUnit(null);
              setToast(`${updated.name}: size and color saved`);
            } catch (reason) {
              setError(reason instanceof Error ? reason.message : "Size and color could not be saved");
            }
          }}
        />
      )}
      {toast && <Toast message={toast} tone="success" onClose={() => setToast("")} />}
    </div>
  );
}

function DashboardCustomEditor({ unit, onClose, onSave }: {
  unit: InventoryUnit;
  onClose: () => void;
  onSave: (details: CustomItemDetailsValue) => Promise<void>;
}) {
  const [details, setDetails] = useState(() => customItemDetailsFromRecord(
    unit.container_type,
    unit.container_color,
    dimensionsFromRecord(unit.length, unit.width, unit.height),
    unit.inventory_type
  ));
  const [saving, setSaving] = useState(false);

  return (
    <div className="modal-backdrop">
      <section className="modal-sheet dashboard-dimension-sheet" role="dialog" aria-modal="true" aria-labelledby="dashboard-size-title">
        <header>
          <div>
            <h2 id="dashboard-size-title">Edit size &amp; color</h2>
            <p>{unit.stock_number} · {unit.name}</p>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Close size editor"><X /></button>
        </header>
        <CustomItemDetailsFields value={details} onChange={setDetails} />
        <div className="form-actions">
          <button className="button secondary" onClick={onClose}>Cancel</button>
          <button className="button primary" disabled={saving} onClick={async () => {
            setSaving(true);
            await onSave(details);
            setSaving(false);
          }}>{saving ? "Saving…" : "Save size & color"}</button>
        </div>
      </section>
    </div>
  );
}

function formatTime(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }).format(new Date(value));
}
